// useVacancyApplicantActivityFlow.ts
import { ActivityApplicationConstants } from "@/apps/tatar/activityApp/ActivityHooks";
import { APActivity } from "@/apps/tatar/activityApp/ActivityInterfaces";
import { AssetTypes } from "@/model/AssetTypes";
import { ActionFlowEntryDataCreate } from "@/modules/action-flow/model/action-flow.interface";
import DataBusDefaults from "@/services/DataBusDefaults";
import i18n from "@/i18n";
import { useActionFlowEntries } from "./useActionFlowEntries";
import {
  VacancyApplicant,
  VacancyApplication,
} from "../../model/VacancyApplicant.interface";
import {
  VacancyActionflowIndentifier,
  VacancyActivityMeta,
} from "../../model/VacancyActivity.interface";
import CacheService from "@/services/CacheService";
import { OAObject } from "@/apps/tatar/objectsApp/types/object.interface";
import StringUtils from "@/utils/StringUtils";
import Log from "@/debug/Log";
import { HTTP } from "@/utils/Http";
import moment from "moment";
import { EmailUser } from "@/modules/comments-module/Comments";
import CommentsService from "@/modules/comments-module/CommentsService";
import VacancyOfferUtils from "../../vacancy-applicant/utils/VacancyOfferUtils";
import ReactDOMServer from "react-dom/server";
import ModalManager from "@/components/ModalComponent/ModalManager";
import VacancyRentCoverageForm from "../../vacancy-applicant/components/vacancy-applicant-entry/VacancyRentCoverageForm";
import { NewAttachmentValue } from "@/modules/customfields/fields/CFAttachment";
import { nanoid } from "nanoid";
import CDNService from "@/services/CDNService";
import { useState } from "react";

type Props = {
  activity: APActivity;
  constants: ActivityApplicationConstants;
};

const useVacancyApplicantActivityFlow = (props: Props) => {
  const { activity, constants } = props;

  const {
    isLoading: isLoadingActionFlow,
    isEmpty,
    error,
    warning,
    offerActionFlowEntry,
    createActionFlowEntry,
    createActionFlowEntryAsync,
    deleteActionFlowEntry,
  } = useActionFlowEntries({ activity, constants });

  const [isCreating, setIsCreating] = useState<boolean>(false);

  const createReplacements = async () => {
    try {
      const object = (await CacheService.getData({
        id: activity.data.objectId,
        oType: "asset",
        assetType: AssetTypes.Portfolio.Object,
        ignoreDelay: true,
      })) as OAObject;

      const extra = activity.data.meta as VacancyActivityMeta;
      const address = object.data.feature?.address?.address?.[0];

      const replacements = {
        objectStreet: address?.streetname || "-",
        objectStreetNumber: address?.streetnumber || "-",
        objectPostalCode: address?.postalcode || "-",
        objectCity: address?.city || "-",
        yearOfConstruction: object.data.feature?.immo?.constructionYear || "-",
        rentalUnitDisplayName: activity.data.displayName,
        planRent: StringUtils.formatCurrency(extra.planRent),
        rentalUnitArea: StringUtils.formatArea(extra.area),
        rent: StringUtils.formatCurrency(extra.planRent),
        heatingCostsIncluded: i18n.t(
          `Global.Labels.${extra.heatingCostsIncluded}`
        ),
        additionalCosts: StringUtils.formatCurrency(extra.additionalCosts),
        rentTotalCosts: StringUtils.formatCurrency(
          (extra?.planRent || 0) + (extra.additionalCosts || 0)
        ),
        deposit: StringUtils.formatCurrency((extra?.planRent || 0) * 3),
      };

      return replacements;
    } catch (error) {
      throw new Error("Error creating replacements: " + error);
    }
  };

  const checkRequiredFieldsForQrCode = (): boolean => {
    const activityMeta = activity.data.meta as VacancyActivityMeta;
    if (!activityMeta) {
      DataBusDefaults.toast({
        type: "error",
        text: i18n.t(
          "apTemplate:Vacancy.OfferActionFlow.QR.NoMeta",
          "Es ist ein Fehler beim Erstellen des QR Codes aufgetreten. Bitte füllen Sie alle Daten für diesen Leerstand aus. (Planmiete, Fläche, Nebenkosten etc.)"
        ),
      });
      return false;
    }

    const { planRent, additionalCosts, area, heatingCostsIncluded } =
      activityMeta;

    if (planRent === undefined || planRent === null) {
      DataBusDefaults.toast({
        type: "error",
        text: i18n.t(
          "apTemplate:Vacancy.OfferActionFlow.QR.PlanRentRequired",
          "Es ist ein Fehler beim Erstellen des QR Codes aufgetreten. Bitte geben Sie die Planmiete an."
        ),
      });
      return false;
    }
    if (additionalCosts === undefined || additionalCosts === null) {
      DataBusDefaults.toast({
        type: "error",
        text: i18n.t(
          "apTemplate:Vacancy.OfferActionFlow.QR.AdditionalCostsRequired",
          "Es ist ein Fehler beim Erstellen des QR Codes aufgetreten. Bitte geben Sie die Nebenkosten an."
        ),
      });
      return false;
    }
    if (area === undefined || area === null) {
      DataBusDefaults.toast({
        type: "error",
        text: i18n.t(
          "apTemplate:Vacancy.OfferActionFlow.QR.AreaRequired",
          "Es ist ein Fehler beim Erstellen des QR Codes aufgetreten. Bitte geben Sie die Fläche an."
        ),
      });
      return false;
    }
    if (heatingCostsIncluded === undefined || heatingCostsIncluded === null) {
      DataBusDefaults.toast({
        type: "error",
        text: i18n.t(
          "apTemplate:Vacancy.OfferActionFlow.QR.HeatingCostsRequired",
          "Es ist ein Fehler beim Erstellen des QR Codes aufgetreten. Bitte geben Sie an, ob die Heizkosten in der Miete enthalten sind."
        ),
      });
      return false;
    }
    return true;
  };

  const createSelfDisclosureActionFlow = async () => {
    try {
      if (!checkRequiredFieldsForQrCode()) {
        return;
      }
      const replacements = await createReplacements();
      const actionFlowEntry: ActionFlowEntryDataCreate = {
        actionFlowEntryType: "public",
        linkedAsset: {
          assetId: activity._id,
          assetType: constants.assetType,
        },
        replacementData: replacements,
        type: activity.data.type,
        identifier: "vacancy-self-disclosure",
      };

      createActionFlowEntry(
        actionFlowEntry,
        i18n.t(
          "apTemplate:Vacancy.OfferActionFlow.CreateSuccess",
          "Der QR Code wurde erfolgreich erstellt."
        ),
        i18n.t(
          "apTemplate:Vacancy.OfferActionFlow.CreateError",
          "Es ist ein Fehler beim Erstellen des QR-Codes aufgetreten. Bitte versuchen Sie es erneut."
        )
      );
    } catch (error) {
      Log.error("useVacancyApplicantActivityFlow", error);
      DataBusDefaults.toast({
        type: "error",
        text: i18n.t(
          "apTemplate:Vacancy.OfferActionFlow.CreateError",
          "Es ist ein Fehler beim Erstellen des QR-Codes aufgetreten. Bitte versuchen Sie es erneut."
        ),
      });
    }
  };

  const createGuarantorActionFlow = async (
    application: VacancyApplication,
    applicantId: string
  ) => {
    try {
      const replacements = await createReplacements();
      const applicant = VacancyOfferUtils.getApplicantById(
        application,
        applicantId
      ) as VacancyApplication["data"]["applicants"][number];

      const applicantName = `${applicant?.firstName || ""} ${
        applicant.lastName || "-"
      }`;

      const applicantAddress = `${applicant?.street} ${applicant?.streetNumber}, ${applicant?.zipCode} ${applicant?.city}`;
      const actionFlowEntry: ActionFlowEntryDataCreate = {
        actionFlowEntryType: "public",
        linkedAsset: {
          assetId: application._id,
          assetType: AssetTypes.ActivityRelated.VacancyApplication,
          extra: {
            applicantId: applicant.id,
            activityId: activity._id,
          },
        },
        replacementData: { ...replacements, applicantName, applicantAddress },
        type: activity.data.type,
        identifier: "vacancy-guarantor-disclosure",
      };

      const actionFlowEntryId = await createActionFlowEntryAsync(
        actionFlowEntry
      );

      const entry = await CacheService.getData({
        id: actionFlowEntryId,
        oType: "asset",
        assetType: AssetTypes.ActionFlow.ActionFlowEntry,
      });

      // 2. update Applicant
      const response = await HTTP.patch({
        url: `/${constants?.serviceUrl}/applicant/${application._id}`,
        target: "EMPTY",
        bodyParams: {
          data: {
            applicants: [
              ...application.data.applicants.filter(
                (a) => a.id !== applicantId
              ),
              {
                ...applicant,
                currentActionFlow: {
                  identifier: "vacancy-guarantor-disclosure",
                  actionFlowEntryId: actionFlowEntryId,
                  date: moment(),
                  status: "open",
                },
              },
            ],
          },
        },
      });

      // 3. after success send the email
      const emailUser: EmailUser = {
        address: applicant.email,
        name: `${applicant.firstName} ${applicant.lastName}`,
      };

      const link = VacancyOfferUtils.getTokenLink(entry);
      const rentalUnitName = activity.data.displayName;

      const object = (await CacheService.getData({
        id: activity.data.objectId,
        oType: "asset",
        assetType: AssetTypes.Portfolio.Object,
      })) as OAObject;

      const address = object.data.feature?.address?.address?.[0];
      const rentalUnitAddress = `${address?.streetname} ${address?.streetnumber}, ${address?.postalcode} ${address?.city}`;

      await CommentsService.sendMail({
        assetType: constants.assetType,
        assetId: activity._id,
        message: getGuarantorMailTemplate({
          url: link,
          applicant,
          rentalUnitName,
          rentalUnitAddress,
        }),
        subject: `${i18n.t(
          "apTemplate:Vacancy.Applicant.GuarantorRequest.Subject",
          "Bürgschaftsanfrage für Ihre Wohnungsbewerbung"
        )}`,
        recipients: [emailUser],
      });

      // 3.1 after success show toast
      DataBusDefaults.toast({
        type: "success",
        text: i18n.t(
          "apTemplate:Vacancy.Applicant.GuarantorRequest.Success",
          "Anfrage zur Bürgschaft erfolgreich versendet."
        ),
      });

      CacheService.updateDataInCaches(response._id, response);

      // 3. Send link to applicant
    } catch (error) {
      Log.error("useVacancyApplicantActivityFlow", error);
      DataBusDefaults.toast({
        type: "error",
        text: i18n.t(
          "apTemplate:Vacancy.Applicant.GuarantorRequest.Error",
          "Fehler beim Versenden der Anfrage zur Bürgschaft."
        ),
      });
    }
  };

  const createRequestApplicantInfoActionFlow = async (
    application: VacancyApplication,
    applicantId: string
  ) => {
    try {
      const replacements = await createReplacements();
      const applicant = VacancyOfferUtils.getApplicantById(
        application,
        applicantId
      ) as VacancyApplication["data"]["applicants"][number];

      const applicantName = `${applicant?.firstName || ""} ${
        applicant.lastName || "-"
      }`;

      const actionFlowEntry: ActionFlowEntryDataCreate = {
        actionFlowEntryType: "public",
        linkedAsset: {
          assetId: application._id,
          assetType: AssetTypes.ActivityRelated.VacancyApplication,
          extra: {
            applicantId: applicant.id,
            activityId: activity._id,
          },
        },
        replacementData: { ...replacements, applicantName },
        type: activity.data.type,
        identifier: "vacancy-applicant-info-disclosure",
      };

      const actionFlowEntryId = await createActionFlowEntryAsync(
        actionFlowEntry
      );

      const entry = await CacheService.getData({
        id: actionFlowEntryId,
        oType: "asset",
        assetType: AssetTypes.ActionFlow.ActionFlowEntry,
      });

      // 2. update Applicant
      const response = await HTTP.patch({
        url: `/${constants?.serviceUrl}/applicant/${application._id}`,
        target: "EMPTY",
        bodyParams: {
          data: {
            applicants: [
              ...application.data.applicants.filter(
                (a) => a.id !== applicantId
              ),
              {
                ...applicant,
                currentActionFlow: {
                  identifier: "vacancy-applicant-info-disclosure",
                  actionFlowEntryId: actionFlowEntryId,
                  date: moment(),
                  status: "open",
                },
              },
            ],
          },
        },
      });

      // 3. after success send the email
      const emailUser: EmailUser = {
        address: applicant.email,
        name: `${applicant.firstName} ${applicant.lastName}`,
      };

      const link = VacancyOfferUtils.getTokenLink(entry);
      const rentalUnitName = activity.data.displayName;

      const object = (await CacheService.getData({
        id: activity.data.objectId,
        oType: "asset",
        assetType: AssetTypes.Portfolio.Object,
      })) as OAObject;

      const address = object.data.feature?.address?.address?.[0];
      const rentalUnitAddress = `${address?.streetname} ${address?.streetnumber}, ${address?.postalcode} ${address?.city}`;

      await CommentsService.sendMail({
        assetType: constants.assetType,
        assetId: activity._id,
        message: getApplicantMailTemplate({
          url: link,
          applicant,
          rentalUnitName,
          rentalUnitAddress,
        }),
        subject: `${i18n.t(
          "apTemplate:Vacancy.Applicant.DataRequest.Subject",
          "Bitte ergänzen Sie Daten für Ihre Wohnungsbewerbung"
        )}`,
        recipients: [emailUser],
      });

      // 3.1 after success show toast
      DataBusDefaults.toast({
        type: "success",
        text: i18n.t(
          "apTemplate:Vacancy.Applicant.DataRequest.Success",
          "Anfrage zur Datenvervollständigung erfolgreich versendet."
        ),
      });

      CacheService.updateDataInCaches(response._id, response);

      // 3. Send link to applicant
    } catch (error) {
      Log.error("useVacancyApplicantActivityFlow", error);
      DataBusDefaults.toast({
        type: "error",
        text: i18n.t(
          "apTemplate:Vacancy.Applicant.DataRequest.Error",
          "Fehler beim Versenden der Anfrage zur Datenvervollständigung."
        ),
      });
    }
  };

  const deleteSelfDisclosureActionFlow = (id: string) => {
    deleteActionFlowEntry(
      id,
      i18n.t(
        "apTemplate:Vacancy.OfferActionFlow.QR.DeleteSuccess",
        "Der QR Code wurde erfolgreich gelöscht."
      ),
      i18n.t(
        "apTemplate:Vacancy.OfferActionFlow.QR.DeleteError",
        "Beim Löschen des QR Codes ist ein Fehler aufgetreten."
      )
    );
  };

  const openRentCoverageActionFlowModal = (
    application: VacancyApplication,
    applicant: VacancyApplicant,
    onSuccess?: () => void
  ) => {
    ModalManager.show({
      title: (
        <div className="padding-16">
          {i18n.t(
            "apTemplate:Vacancy.OfferActionFlow.RentCoverage.Title",
            "Mietdeckungsbescheinigung anfordern"
          )}
        </div>
      ),
      ignoreEscapeToClose: true,
      noPadding: true,
      content: (
        _states: any,
        _setStates: (states: any) => void,
        closeModal?: () => void
      ) => (
        <VacancyRentCoverageForm
          onSuccess={async (rentOffer: NewAttachmentValue) => {
            await createRentCoverageActionFlow(
              application,
              applicant,
              rentOffer
            );
            closeModal?.();
            onSuccess?.();
          }}
          onCancel={closeModal}
        />
      ),
    });
  };

  const uploadRentOfferAttachment = async (rentOfferFile: File) => {
    try {
      const result = CDNService.uploadFile({
        assetField: "data.attachments",
        file: rentOfferFile,
        filename: rentOfferFile.name,
        assetType: AssetTypes.ActivityRelated.VacancyApplication,
        tempID: nanoid(),
      });

      return result;
    } catch (error) {
      Log.error("useVacancyApplicantActivityFlow", error);
      DataBusDefaults.toast({
        type: "error",
        text: i18n.t(
          "apTemplate:Vacancy.OfferActionFlow.RentCoverage.UploadError",
          "Fehler beim Hochladen der Mietbescheinigung."
        ),
      });
    }
  };

  const createRentCoverageActionFlow = async (
    application: VacancyApplication,
    applicant: VacancyApplicant,
    rentOffer: NewAttachmentValue
  ) => {
    try {
      setIsCreating(true);
      const mainContactPerson = application.data.applicants.find(
        (c) => c.isMain
      );
      if (!mainContactPerson) {
        DataBusDefaults.toast({
          type: "error",
          text: i18n.t(
            "apTemplate:Vacancy.OfferActionFlow.RentCoverage.NoContractPerson",
            "Beim versenden des Mietangebots ist ein Fehler aufgetreten. Es wurde kein Hauptmieter gefunden."
          ),
        });
        return;
      }

      // 1. Upload rent offer attachment to cdn
      const offerAttachment = await uploadRentOfferAttachment(rentOffer.file);

      const replacements = {
        fullName: `${mainContactPerson.firstName} ${mainContactPerson.lastName}`,
      };

      // 2. create ActionFlowEntry
      const actionFlowEntry: ActionFlowEntryDataCreate = {
        actionFlowEntryType: "public",
        linkedAsset: {
          assetType: AssetTypes.ActivityRelated.VacancyApplication,
          assetId: application._id,
          extra: {
            applicantId: applicant.id,
            activityId: activity._id,
          },
        },
        replacementData: replacements,
        type: activity.data.type,
        identifier: VacancyActionflowIndentifier.RentCoverage,
      };

      const actionFlowEntryId = await createActionFlowEntryAsync(
        actionFlowEntry
      );

      // 3. get entry object
      const entry = await CacheService.getData({
        id: actionFlowEntryId as string,
        oType: "asset",
        assetType: AssetTypes.ActionFlow.ActionFlowEntry,
      });

      const link = VacancyOfferUtils.getTokenLink(entry);

      // 4. Prepare email data
      const emailUser: EmailUser = {
        address: applicant.email,
        name: `${applicant.firstName} ${applicant.lastName}`,
      };

      const rentalUnitName = activity.data.displayName;

      const object = (await CacheService.getData({
        id: activity.data.objectId,
        oType: "asset",
        assetType: AssetTypes.Portfolio.Object,
      })) as OAObject;

      const address = object.data.feature?.address?.address?.[0];
      const rentalUnitAddress = `${address?.streetname} ${address?.streetnumber}, ${address?.postalcode} ${address?.city}`;

      // 5. Send email with rent offer
      await CommentsService.sendMail({
        assetType: constants.assetType,
        assetId: activity._id,
        message: getRentCoverageMailTemplate({
          url: link,
          applicant,
          rentalUnitName,
          rentalUnitAddress,
        }),
        subject: `${i18n.t(
          "apTemplate:Vacancy.Applicant.RentCoverage.Subject",
          "Mietangebot und Antrag auf Mietkostenübernahme"
        )}`,
        attachments: [offerAttachment.cdnID],
        recipients: [emailUser],
      });

      // 6. Update applicant with new action flow
      const response = await HTTP.patch({
        url: `/${constants?.serviceUrl}/applicant/${application._id}`,
        target: "EMPTY",
        bodyParams: {
          data: {
            applicants: [
              ...application.data.applicants.filter(
                (a) => a.id !== applicant.id
              ),
              {
                ...applicant,
                currentActionFlow: {
                  identifier: VacancyActionflowIndentifier.RentCoverage,
                  actionFlowEntryId: actionFlowEntryId,
                  date: moment(),
                  status: "open",
                },
              },
            ],
          },
        },
      });

      CacheService.updateDataInCaches(response._id, response);
      CacheService.updateDataInTables(response._id, response);
      DataBusDefaults.toast({
        type: "success",
        text: i18n.t(
          "apTemplate:Vacancy.OfferActionFlow.RentCoverage.CreateSuccess",
          "Das Mietangebot wurde erfolgreich versendet."
        ),
      });
    } catch (error) {
      Log.error("useVacancyApplicantActivityFlow", error);
      DataBusDefaults.toast({
        type: "error",
        text: i18n.t(
          "apTemplate:Vacancy.OfferActionFlow.RentCoverage.CreateError",
          "Beim Versenden des Mietangebots ist ein Fehler aufgetreten."
        ),
      });
    } finally {
      setIsCreating(false);
    }
  };

  return {
    isLoading: isLoadingActionFlow || isCreating,
    isEmpty,
    error,
    warning,
    offerActionFlowEntry,
    createSelfDisclosureActionFlow,
    openRentCoverageActionFlowModal,
    deleteSelfDisclosureActionFlow,
    createGuarantorActionFlow,
    createRequestApplicantInfoActionFlow,
  };
};

export default useVacancyApplicantActivityFlow;

type GuarantorMailTemplateProps = {
  url: string;
  applicant: VacancyApplicant;
  rentalUnitName: string;
  rentalUnitAddress: string;
};

const getGuarantorMailTemplate = (props: GuarantorMailTemplateProps) => {
  return ReactDOMServer.renderToString(
    <>
      <GuarantorMailTemplate {...props} /> <VacancyMailSignature />
    </>
  );
};

const GuarantorMailTemplate = (props: GuarantorMailTemplateProps) => {
  const { url, rentalUnitAddress, rentalUnitName, applicant } = props;

  const salutationText = VacancyOfferUtils.getSalutationText(applicant);

  return (
    <div>
      <p>{salutationText},</p>
      <p>
        im Rahmen der Bewerbung um die Mietwohnung{" "}
        <strong>"{rentalUnitName}"</strong> in
        <strong>{rentalUnitAddress}</strong> bitten wir Sie, uns einen
        geeigneten Bürgen zur Unterstützung Ihre Bewerbung zu benennen.
      </p>
      <p>
        Nutzen Sie bitte folgenden Link, um das Bürgschaftsformular auszufüllen:
      </p>
      <p>
        <a href={url}>Bürgschaftsformular</a>
      </p>
      <p>
        Für Rückfragen und weitere Informationen stehen wir Ihnen
        selbstverständlich zur Verfügung.
      </p>
    </div>
  );
};

const getApplicantMailTemplate = (props: GuarantorMailTemplateProps) => {
  return ReactDOMServer.renderToString(
    <>
      <ApplicantMailTemplate {...props} />
      <VacancyMailSignature />
    </>
  );
};

const ApplicantMailTemplate = (props: GuarantorMailTemplateProps) => {
  const { url, applicant, rentalUnitAddress, rentalUnitName } = props;

  const salutationText = VacancyOfferUtils.getSalutationText(applicant);

  return (
    <div>
      <p>{salutationText},</p>
      <p>
        im Rahmen der Bewerbung um die Mietwohnung{" "}
        <strong>"{rentalUnitName}"</strong> in
        <strong>{rentalUnitAddress}</strong> bitten wir Sie, Ihre Daten zu
        vervollständigen.
      </p>
      <p>
        Nutzen Sie bitte folgenden Link, um das Selbstauskunft Formualr
        auszufüllen:
      </p>
      <p>
        <a href={url}>Selbstauskunft</a>
      </p>
      <p>
        Für Rückfragen und weitere Informationen stehen wir Ihnen
        selbstverständlich zur Verfügung.
      </p>
    </div>
  );
};

export const VacancyMailSignature = () => {
  return (
    <div>
      <br></br>
      <p>
        Mit freundlichen Grüßen<br></br>Team Vermietung
      </p>
      <p>
        Tatar Holding
        <br></br>Bahnhofstrasse 1-3
        <br></br>33102 Paderborn
        <br></br>Germany
        <br></br>_________________________________________
        <br></br>T: <a href="tel:+495251547310">+49 5251 547 31 0</a>
        <br></br>F: <a href="tel:+4952515473199">+49 5251 547 31 99</a>
        <br></br>
        <br></br>
        <a href="mailto:vermietung@tatar.de">vermietung@tatar.de</a>
        <br></br>
        <a
          href="https://www.tatar.de"
          target="_blank"
          rel="noopener noreferrer"
        >
          www.tatar.de
        </a>
      </p>
      <p>
        Disclaimer:<br></br>Über das Internet versandte E-Mails können leicht
        verfälscht oder unter fremden Namen erstellt werden. Demzufolge müssen
        wir zu Ihrer und unserer Sicherheit die rechtliche Verbindlichkeit der
        vorstehenden Erklärungen ausschließen. Der Versand von E-Mails dient
        somit ausschließlich dem Informationsaustausch. Diese E-Mail
        einschließlich eventuell angehängter Dateien enthält möglicherweise
        vertrauliche und/oder rechtlich geschützte Informationen. Wenn Sie nicht
        der richtige Adressat sind und diese E-Mail irrtümlich erhalten haben,
        dürfen Sie weder den Inhalt dieser E-Mail nutzen noch dürfen Sie die
        eventuell angehängten Dateien öffnen und auch nichts kopieren oder
        weitergeben/verbreiten. Bitte verständigen Sie den Absender und löschen
        Sie diese E-Mail und eventuell angehängte Dateien umgehend. Vielen Dank
        für Ihre Mithilfe und Ihr Verständnis
      </p>
    </div>
  );
};

const getRentCoverageMailTemplate = (props: GuarantorMailTemplateProps) => {
  return ReactDOMServer.renderToString(
    <>
      <RentCoverageMailTemplateProps {...props} /> <VacancyMailSignature />
    </>
  );
};
const RentCoverageMailTemplateProps = (props: GuarantorMailTemplateProps) => {
  const { url, rentalUnitAddress, rentalUnitName, applicant } = props;

  const salutationText = VacancyOfferUtils.getSalutationText(applicant);

  return (
    <div>
      <p>{salutationText},</p>
      <p>
        im Rahmen der Bewerbung um die Mietwohnung{" "}
        <strong>"{rentalUnitName}"</strong> in
        <strong>{rentalUnitAddress}</strong> erhalten Sie hiermit unser
        offizielles Mietangebot um die Übernahme der Mietkosten durch das
        Jobcenter zu beantragen.
      </p>
      <p>
        Wenn sie einen Nachweis über die Mietkostenübernahme erhalten haben,
        können Sie diesen bitte über den unten stehenden Link hochladen:
      </p>
      <p>
        <a href={url}>Mietkostenübernahme-Formular hochladen</a>
      </p>
      <p>
        Für Rückfragen und weitere Informationen stehen wir Ihnen
        selbstverständlich zur Verfügung.
      </p>
    </div>
  );
};
